export interface JuiceParticle {
  readonly kind: 'juice';
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  alpha: number;
}

export interface HalfFruitParticle {
  readonly kind: 'half';
  readonly emoji: string;
  /** When set, slice halves use this PNG instead of `emoji` (fruit-ninja-without-score). */
  readonly fruitImageSrc?: string;
  /** Second half of a pair: mirror so two `*_half.png` copies face each other. */
  readonly mirrorHalf?: boolean;
  readonly radius: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  angularVel: number;
  alpha: number;
}

export interface ScorePopup {
  x: number;
  y: number;
  vy: number;
  text: string;
  color: string;
  alpha: number;
}

export type Particle = JuiceParticle | HalfFruitParticle;

const GRAVITY = 0.18;
const FADE = 0.96;
const REF_FRAME_MS = 16.6;

/** `dtMs` — время с прошлого тика; при 60fps k=1, при 30fps k=2. Позволяет не тормозить частицы на слабом TV. */
export function updateParticle(p: Particle, dtMs: number = REF_FRAME_MS): void {
  const k = dtMs / REF_FRAME_MS;
  p.x += p.vx * k;
  p.y += p.vy * k;
  p.vy += GRAVITY * k;
  p.alpha *= FADE ** k;
  if (p.kind === 'half') {
    p.rotation += p.angularVel * k;
  }
}

export function isAlive(p: Particle): boolean {
  return p.alpha > 0.02;
}

export function spawnJuice(
  x: number,
  y: number,
  color: string,
  count = 18,
): JuiceParticle[] {
  return Array.from({ length: count }, () => {
    const angle = Math.random() * Math.PI * 2;
    const speed = 2 + Math.random() * 4;
    return {
      kind: 'juice' as const,
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 2,
      radius: 3 + Math.random() * 5,
      color,
      alpha: 1,
    };
  });
}

export function spawnHalves(
  emoji: string,
  radius: number,
  x: number,
  y: number,
  sliceDx: number,
  sliceDy: number,
  rotation: number,
  fruitImageSrc?: string,
): HalfFruitParticle[] {
  const angle = Math.atan2(sliceDy, sliceDx);
  const perp = angle + Math.PI / 2;

  return ([-1, 1] as const).map((side) => ({
    kind: 'half' as const,
    emoji,
    ...(fruitImageSrc !== undefined ? { fruitImageSrc } : {}),
    ...(fruitImageSrc !== undefined ? { mirrorHalf: side === 1 } : {}),
    radius: radius * 0.85,
    x: x + Math.cos(perp) * side * radius * 0.4,
    y: y + Math.sin(perp) * side * radius * 0.4,
    vx: Math.cos(perp) * side * 3 + sliceDx * 0.2,
    vy: Math.sin(perp) * side * 3 + sliceDy * 0.2 - 2,
    rotation,
    angularVel: side * 0.15,
    alpha: 1,
  }));
}

export function spawnExplosion(x: number, y: number, count = 30): JuiceParticle[] {
  return Array.from({ length: count }, (_, i) => {
    const angle = Math.random() * Math.PI * 2;
    const speed = 3 + Math.random() * 6;
    return {
      kind: 'juice' as const,
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 3,
      radius: 4 + Math.random() * 6,
      color: i % 2 === 0 ? '#ff6600' : '#ffcc00',
      alpha: 1,
    };
  });
}
